import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, Check, Copy } from "lucide-react";
import type { CreateAdminUserResponse } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { FormField } from "@/shared/components/form/FormField";
import { Label } from "@/shared/components/ui/Label";
import { useCreateAdminUser } from "@/shared/lib/admin";

export function AdminUserNew() {
  const navigate = useNavigate();
  const create = useCreateAdminUser();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [result, setResult] = useState<CreateAdminUserResponse | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    create.mutate(
      {
        email: email.trim(),
        name: name.trim() || undefined,
        isAdmin,
      },
      { onSuccess: (data) => setResult(data) },
    );
  }

  return (
    <Section spacing="md">
      <Container size="2xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to="/app/admin/users"
              className="inline-flex items-center gap-1 type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
              Users
            </Link>
            <h1 className="type-heading-lg text-text">New user</h1>
          </div>

          <form
            className="flex flex-col gap-6 rounded-2xl glass-surface p-6"
            onSubmit={handleSubmit}
          >
            <FormField
              label="Email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <FormField
              label="Name"
              hint="Optional display name."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="mx-4 flex items-center gap-3">
              <Checkbox
                id="isAdmin"
                checked={isAdmin}
                onCheckedChange={(v) => setIsAdmin(v === true)}
              />
              <Label htmlFor="isAdmin">Grant admin access</Label>
            </div>

            {create.error ? (
              <p className="mx-4 type-body-sm text-danger-strong">
                {create.error.message}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || !email.trim()}>
                {create.isPending ? "Creating…" : "Create user"}
              </Button>
            </div>
          </form>
        </div>
      </Container>

      {result ? (
        <GeneratedPasswordDialog
          result={result}
          onDone={() => navigate(`/app/admin/users/${result.user.id}`)}
        />
      ) : null}
    </Section>
  );
}

function GeneratedPasswordDialog({
  result,
  onDone,
}: {
  result: CreateAdminUserResponse;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(result.generatedPassword);
      setCopied(true);
    } catch {
      // Clipboard blocked (e.g. insecure context) — the password is still
      // visible on screen for the admin to copy manually.
    }
  }

  return (
    // Not dismissable by clicking out: the password is shown only once.
    <Dialog open onOpenChange={(next) => !next && onDone()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Account created</DialogTitle>
          <DialogDescription>
            Share this password with <strong>{result.user.email}</strong> over a
            secure channel. It is shown only once and cannot be retrieved later.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 rounded-lg glass-surface-subtle px-4 py-3">
          <code className="type-body-base text-text break-all">
            {result.generatedPassword}
          </code>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={copy}
            aria-label="Copy password"
          >
            {copied ? (
              <Check aria-hidden className="h-4 w-4" />
            ) : (
              <Copy aria-hidden className="h-4 w-4" />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onDone}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
